import * as d3 from 'd3';
import * as _ from 'lodash';

import { IChartOptions } from '../../models/IChartOptions';
import { IChart } from '../interfaces/IChart';
import { IPaneConfiguration } from '../interfaces/IPaneConfiguration';
import { ChartFactory } from '../factories/ChartFactory';
import { IPane } from '../pieces';
import { TimeScale } from '../pieces/scales';
import { SvgManager } from '../SvgManager';
import { ILogger, LoggerFactory } from '../../helpers/helpers';
import { TimeChartBase } from './TimeChartBase';
import { INewSeries } from '../managers/INewSeries';
import { setSizeClasses } from './setSizeClasses';
import recomputeTranslates from './recomputeTranslates';

export interface ILineChart extends IChart {}

export class LineChart extends TimeChartBase implements ILineChart {
    private _createdPanes: IPane[];
    private _crosshairCallback;
    private _dispatch: any;
    private _factory = new ChartFactory(d3, console);
    private _hasAbsolutePanesHeight: boolean;
    private _localSeries;
    private _logger: ILogger;
    private _panesScale;
    private _zoomOrder: IPane[];

    constructor(options: IChartOptions, private _svgManager: SvgManager) {
        super(options, _svgManager, 'line-chart');
        this._logger = LoggerFactory.getLogger('WideTech.Charting.lineChart', false, 0);
        this._dispatch = d3.dispatch('lineChartZoom', 'selection');
        this._zoomOrder = [];
        super.dispatch().on('zoom', () => this._dispatch.lineChartZoom());
        super.dispatch().on('selection', (d) => this._dispatch.selection(d));
        this._localSeries = [];

        const size = this._svgManager.getSize();
        this.resize()(size.width, size.height);
    }

    updateSeries(newSeriesList: INewSeries[]) {
        try {
            this.sanitize(newSeriesList);
            if (this._createdPanes) {
                this._logger.info('Updating panes.');
                this._localSeries = newSeriesList;
                this._createdPanes.forEach((p, i) => {
                    let currentSeries = newSeriesList.filter((s) => {
                        return s.pane === i;
                    });
                    if (i === 0) {
                        currentSeries = currentSeries.concat(newSeriesList.filter((s) => !s.pane && s.pane !== 0));
                    }
                    this._logger.debug(`${currentSeries.length} series in pane ${i}.`);
                    p.updateSeries(currentSeries);
                });
                this._logger.info('Panes updated.');
            } else {
                super.series(newSeriesList);
            }
            this.updateLabels();
        } catch (e) {
            this._logger.error(e);
        }
    }

    sanitize(newSeriesList: INewSeries[]): INewSeries[] {
        newSeriesList.forEach((ns) => {
            if (ns.serieState) {
                ns.serieState = 1;
            } else {
                ns.serieState = 0;
            }
        });
        return newSeriesList;
    }

    // vodoo to fix click event
    fixLegend() {
        if (this._createdPanes) {
            this._createdPanes.forEach((p) => p.fixLegend());
        } else {
            this.legend().reselectAndResize();
        }
    }

    update(data) {
        if (this._createdPanes) {
            let beginDate: Date;
            if (!super.xMin()) {
                beginDate = data ? d3.min(data, (d: any) => new Date(d.timeStamp as string)) : null;
            } else {
                beginDate = super.xMin();
            }
            let endDate: Date;
            if (!super.xMax()) {
                endDate = data ? d3.max(data, (d: any) => new Date(d.timeStamp as string)) : null;
            } else {
                endDate = super.xMax();
            }
            this._createdPanes.forEach((p) => {
                p.updateRangeAndData(data, { beginDate, endDate });
            });
        } else {
            super.update(data);
        }
    }

    titleText(value?: string): string {
        const result = super.titleText(value);
        if (this._createdPanes && this._createdPanes.length > 0) {
            this._createdPanes[0].title(value);
        }
        return result;
    }

    isTitleVisible(value?: boolean): boolean {
        const result = super.isTitleVisible(value);
        if (this._createdPanes && this._createdPanes.length > 0) {
            this._createdPanes[0].isTitleVisible(value);
        }
        return result;
    }

    isLegendVisible(value?: boolean): boolean {
        const result = super.isLegendVisible(value);
        if (this._createdPanes) {
            this._createdPanes.forEach((p) => {
                const isVisible = p.isLegendVisible();
                const hasChanged = !(isVisible && value);
                p.isResizedByLegend(result);
                p.isLegendVisible(hasChanged ? value : result && isVisible);
            });
        }
        return result;
    }

    isHorizontalGridlinesVisible(value?: boolean): boolean {
        const result = super.isHorizontalGridlinesVisible(value);
        if (this._createdPanes && this._createdPanes.length > 0) {
            this._createdPanes[0].isHorizontalGridLinesVisible(value);
        }
        return result;
    }

    isXAxisVisible(value?: boolean): boolean {
        const result = super.isXAxisVisible(value);
        if (this._createdPanes && arguments.length > 0) {
            this._createdPanes.forEach((p) => p.isXaxisVisible(result));
        }
        return result;
    }

    isYAxisVisible(value?: boolean): boolean {
        const result = super.isYAxisVisible(value);
        if (this._createdPanes && arguments.length > 0) {
            this._createdPanes.forEach((p) => p.isYaxisVisible(result));
        }
        return result;
    }

    isZoomed() {
        let result: boolean;
        if (this._createdPanes) {
            result = this._zoomOrder.length > 0;
        } else {
            result = super.isZoomed();
        }
        return result;
    }

    resetZoom() {
        if (this._createdPanes) {
            this._zoomOrder.pop().resetZoom();
        } else {
            super.resetZoom();
        }
    }

    resetSelection() {
        if (this._createdPanes) {
            this._createdPanes.forEach((p) => {
                p.resetSelection();
            });
        } else {
            super.resetSelection();
        }
    }

    pan() {
        if (this._createdPanes) {
            this._createdPanes.forEach((p) => p.pan());
        } else {
            super.pan();
            this._dispatch.lineChartZoom();
        }
    }

    select() {
        if (this._createdPanes) {
            this._createdPanes.forEach((p) => p.select());
        } else {
            super.select();
            this._dispatch.lineChartZoom();
        }
    }

    dispatch() {
        return this._dispatch;
    }

    xMin(value?: Date): Date {
        const result = super.xMin(value);
        if (this._createdPanes && arguments.length > 0) {
            this._createdPanes.forEach((p) => p.xMin(result));
        }
        return result;
    }

    xMax(value?: Date): Date {
        const result = super.xMax(value);
        if (this._createdPanes && arguments.length > 0) {
            this._createdPanes.forEach((p) => p.xMax(result));
        }
        return result;
    }

    xRange(value?: [Date, Date]): [Date, Date] {
        const result = super.xRange(value);
        if (this._createdPanes && arguments.length > 0) {
            this._createdPanes.forEach((p) => p.xRange(result));
        }
        return result;
    }

    yMin(value?: number): number {
        const result = super.yMin(value);
        if (this._createdPanes && arguments.length > 0) {
            this._createdPanes.forEach((p) => p.yMin(result));
        }
        return result;
    }

    yMax(value?: number): number {
        const result = super.yMax(value);
        if (this._createdPanes && arguments.length > 0) {
            this._createdPanes.forEach((p) => p.yMax(result));
        }
        return result;
    }

    updatePanes(panes: IPaneConfiguration[]) {
        const container = this.group().select('.panes');
        container.selectAll('.pane').data(panes);
    }

    panes(panes: IPaneConfiguration[]) {
        if (panes && panes.length > 0) {
            this._svgManager.clearResizeFunctions();
            const totalHeight = d3.sum(panes, (d: any) => d.height);
            const svgSize = this._svgManager.getSize();
            const frac = this._hasAbsolutePanesHeight ? 1 : svgSize.height / totalHeight;
            const w = svgSize.width;
            if (!this._panesScale) {
                this._panesScale = new TimeScale(w - 230, new Date(2015, 1, 1), new Date(2015, 2, 1));
            }
            const container = this.group().select('.panes');
            container.selectAll('.default-pane').remove();

            this._createdPanes = this._createdPanes ? this._createdPanes : [];
            const extraPanesCount = this._createdPanes.length - panes.length;
            if (extraPanesCount > 0) {
                const spliced = this._createdPanes.splice(panes.length - 1, extraPanesCount);
                spliced.forEach((s) => s.group().remove());
            }
            const newPanes = panes.filter((p, i) => i >= this._createdPanes.length);
            newPanes.forEach((p, i) => {
                this.configureNewpane(p, frac, container, w, panes, i);
            });

            this.updateResizeFunction(totalHeight);

            if (this._hasAbsolutePanesHeight) {
                const selection = this._svgManager.container();
                selection.attr('height', totalHeight + 20);
            }

            const forcedHeight = _.sumBy(panes, (p) => (p.minHeight ? p.minHeight : 0));

            let currentTranslate = 0;
            panes.forEach((p, i) => {
                const ret = this.translatePanes(i, p, frac, forcedHeight, svgSize, currentTranslate, w);
                currentTranslate = ret.currentTranslate;
                this.updatePane(ret.createdPane, p, i);
                this.hookResizePane(ret.createdPane, p, i, totalHeight, forcedHeight, panes);
            });
        }
    }

    private updateResizeFunction(totalHeight: number) {
        this._svgManager.addResizeFunction((width, height) => {
            this.width(width);
            this.height(height);

            setSizeClasses(this.group(), width, height, this.ratio() || 1);

            if (this._hasAbsolutePanesHeight) {
                this._svgManager.container().attr('height', totalHeight + 20);
            }
        });
    }

    private translatePanes(
        i: number,
        p: IPaneConfiguration,
        frac: number,
        forcedHeight: number,
        svgSize: { width: number; height: number },
        currentTranslate: number,
        w: number
    ) {
        const createdPane = this._createdPanes[i];
        const resizeFunction = createdPane.resizeFunction();
        try {
            const h = this.calculateHeight(p, frac, forcedHeight, svgSize.height);
            createdPane.group().attr({
                transform: `translate(0, ${currentTranslate})`
            });
            currentTranslate += h;
            resizeFunction(w, h);
        } catch (e) {
            this._logger.error(e);
        }

        return { currentTranslate, createdPane };
    }

    private hookResizePane(createdPane: IPane, p: IPaneConfiguration, i: number, totalHeight: number, forcedHeight: number, panes: IPaneConfiguration[]) {
        const resizeFunction = createdPane.resizeFunction();
        this._svgManager.addResizeFunction((width, height) => {
            const multiplier = this._hasAbsolutePanesHeight ? 1 : height / totalHeight;
            const curHeight = this.calculateHeight(p, multiplier, forcedHeight, height);

            let translate = 0;
            for (let j = 0; j < i; j++) {
                translate += this.calculateHeight(panes[j], multiplier, forcedHeight, height);
            }
            createdPane.group().attr({ transform: `translate(0, ${translate})` });
            resizeFunction(width, curHeight);
        });
    }

    private configureNewpane(p: IPaneConfiguration, frac: number, container: d3.Selection<any>, w: number, panes: IPaneConfiguration[], i: number) {
        const h = p.height * frac;
        const pane = this.createPane(container, w, h);
        const index = panes.indexOf(p);
        if (this._localSeries) {
            let currentSeries = this._localSeries.filter((s) => s.pane === index);
            if (i === 0) {
                currentSeries = currentSeries.concat(this._localSeries.filter((s) => !s.pane));
            }
            pane.updateSeries(currentSeries);
        }

        pane.updateRange({
            beginDate: super.xMin(),
            endDate: super.xMax()
        });
    }

    private _lineChartRatio: number = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number) {
        if (arguments.length) {
            super.ratio(value);
            this._lineChartRatio = value;
            if (this._createdPanes) {
                this._createdPanes.forEach((p) => p.ratio(value));
            }
            return this;
        } else {
            return this._lineChartRatio;
        }
    }

    private calculateHeight(p: IPaneConfiguration, multiplier: number, forcedHeight: number, height: number) {
        let curHeight = Math.max(multiplier * p.height, p.minHeight ? p.minHeight : 0);
        if (!p.minHeight && curHeight + forcedHeight > height) {
            curHeight = height - forcedHeight;
        }
        return curHeight;
    }

    highlight(values: any[], keyFunction: (d: any) => any, classed: string, color: string) {
        if (this._createdPanes) {
            this._createdPanes.forEach((p) => {
                p.highlight(values, keyFunction, classed, color);
            });
        } else {
            this.series().forEach((s) => s.highlight(values, keyFunction, classed, color));
        }
    }

    setSerieState(serieIdentifier: string, clicked: boolean = false) {
        if (this._createdPanes) {
            this._createdPanes.forEach((p) => p.setSerieState(serieIdentifier, clicked));
        } else {
            this.series().forEach((s) => {
                if (serieIdentifier === (s as any)._identifier) {
                    s.setSerieState(clicked);
                } else {
                    if (!clicked) {
                        s.setSerieState(false, 0, true);
                    }
                }
            });
        }
    }

    hasAbsolutePanesHeight(value?: boolean) {
        if (arguments.length) {
            this._hasAbsolutePanesHeight = value;
        }
        return this._hasAbsolutePanesHeight;
    }

    updateCrosshair(date: Date) {
        if (this._createdPanes) {
            this._createdPanes.forEach((p) => p.updateCrosshair(date));
        } else {
            super.updateCrosshair(date);
        }
    }

    onCrosshair(callback) {
        this._crosshairCallback = callback;
        if (this._createdPanes) {
            this._createdPanes.forEach((p) => p.onCrosshair(callback));
        } else {
            super.onCrosshair(callback);
        }
    }

    private createPane(container: d3.Selection<any>, width: number, height: number) {
        const myPane = this._factory.createLineChartPane(container, width, height);
        const dispatch = myPane.dispatch();
        dispatch.on('panezoom', (data) => {
            this._zoomOrder.push(myPane);
            this._createdPanes.forEach((cp) => cp.updateRange(data));
            this._dispatch.lineChartZoom();
        });
        dispatch.on('panepan', (data) => {
            this._createdPanes.forEach((cp) => cp.updateRange(data));
            this._dispatch.lineChartZoom();
        });
        dispatch.on('paneunzoom', (data) => {
            this._createdPanes.forEach((cp) => cp.updateRange(data));
            this._dispatch.lineChartZoom();
        });
        dispatch.on('selection', (data) => {
            this._dispatch.selection(data);
        });
        if (this._crosshairCallback) {
            myPane.onCrosshair(this._crosshairCallback);
        }
        this._createdPanes.push(myPane);
        return myPane;
    }

    private updatePane(createdPane: IPane, config: IPaneConfiguration, i: number) {
        createdPane.isLegendVisible(this.isLegendVisible() && config.isLegendVisible);
        createdPane.updateConfig(config);
        if (this.hasAbsolutePanesHeight()) {
            createdPane.height(config.height);
        }
    }

    private updateLabels() {
        // only update labels for this trend, not all trend on page
        const selection = this.group().selectAll('.value-label');
        const boundingRectangles = [];
        selection.each(function (d) {
            const boundingClientRect = this.getBoundingClientRect();
            boundingRectangles.push(boundingClientRect);
        });
        const translates = recomputeTranslates(boundingRectangles);
        this.updateLabelPositions(selection, translates);
    }

    private updateLabelPositions(selection: d3.Selection<any>, translates: [number, number][]) {
        selection.attr('transform', function (d, i) {
            const translate = translates[i];
            const select = d3.select(this);
            const existingTranslate = d3.transform(select.attr('transform')).translate;
            const translateX = translate[0] + existingTranslate[0];
            const translateY = translate[1] + existingTranslate[1];
            return `translate(${translateX}, ${translateY})`;
        });
    }
}
